import { forwardRef, useCallback, useState } from "react";
import { Loader, Check, ChevronsDownUp, X, CirclePlus } from "lucide-react";

import { cn, debounce } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";

export type ComboboxOption<T = unknown> = {
  value: string | number;
  label: string;
  raw?: T;
};

export interface ComboboxAsyncProps {
  buttonClassName?: string;
  boxWrapperClassName?: string;
  boxInputClassName?: string;
  boxListClassName?: string;
  boxGroupClassName?: string;
  boxItemClassName?: string;
  options: ComboboxOption[];
  value?: string;
  buttonPlaceholder?: string;
  inputPlaceholder?: string;
  onChange?: (option: ComboboxOption) => void;
  onSearchChange: (value: string) => void;
  onLoading?: boolean;
  defaultChecked?: string;
  labelValueKey?: string;
  clearable?: boolean;
  truncateValue?: number;
  popOverClassName?: string;
  disabled?: boolean;
  onCreateOption?: (search: string) => void;
  createLabel?: string;
}

const ComboboxAsync = forwardRef<HTMLDivElement, ComboboxAsyncProps>(
  (
    {
      buttonClassName,
      boxWrapperClassName,
      boxInputClassName,
      boxListClassName,
      boxGroupClassName,
      boxItemClassName,
      options,
      value,
      onChange,
      onSearchChange,
      onLoading,
      buttonPlaceholder,
      inputPlaceholder,
      labelValueKey = "value",
      clearable = true,
      truncateValue = 0,
      popOverClassName = "",
      disabled = false,
      onCreateOption,
      createLabel = "Add",
    },
    ref
  ) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSearchChange = useCallback(
      debounce((value: string) => {
        onSearchChange(value);
      }, 1000),
      [onSearchChange]
    );

    const onHandleChangeSearchInput = (value: string) => {
      setSearch(value);
      debouncedSearchChange(value);
    };

    const clearInputAndSelection = () => {
      setSearch("");
      if (onChange) {
        onChange?.({ label: "", value: "" });
      }
      onSearchChange("");
    };

    const hasThingsToClear = (): boolean => {
      return !!value || !!search;
    };

    function truncate(str: string, maxLength: number) {
      if (!str) return "";
      return str.length > maxLength ? str.slice(0, maxLength - 1) + "…" : str;
    }

    const comboboxLabel = options.find((option) => {
      const key = labelValueKey as keyof ComboboxOption;
      return option[key] === value;
    })?.label;

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <div className="aero:relative aero:w-full">
          <PopoverTrigger className="aero:w-full" disabled={disabled}>
            <div
              ref={ref}
              role="combobox"
              className={cn(
                "aero:flex aero:w-full aero:items-center aero:justify-between aero:gap-2",
                "aero:h-10 aero:px-4 aero:py-3",
                "aero:bg-white aero:border aero:border-neutral-300 aero:rounded",
                "aero:shadow-none",
                "aero:font-inter aero:font-normal aero:text-sm aero:leading-5",
                "aero:cursor-pointer",
                "aero:focus-visible:outline-none aero:focus-visible:border-ring aero:focus-visible:ring-ring/50 aero:focus-visible:ring-[3px]",
                disabled && "aero:cursor-not-allowed aero:opacity-50",
                buttonClassName
              )}
            >
              <span
                className={cn(
                  "aero:block aero:truncate aero:text-left aero:flex-1 aero:min-w-0",
                  value ? "aero:text-foreground" : "aero:text-neutral-500"
                )}
              >
                {value && !truncateValue
                  ? comboboxLabel
                  : value && truncateValue
                    ? truncate(comboboxLabel ?? "", truncateValue)
                    : buttonPlaceholder
                      ? buttonPlaceholder
                      : "Select Option"}
              </span>
              <ChevronsDownUp className="aero:h-4 aero:w-4 aero:shrink-0 aero:text-neutral-500" />
            </div>
          </PopoverTrigger>
          {clearable && hasThingsToClear() && (
            <Button
              design="text"
              size="iconMd"
              className="aero:!rounded-full aero:absolute aero:right-4 aero:top-1/2 aero:-translate-y-1/2 aero:h-auto aero:w-auto aero:p-1 aero:z-10 aero:bg-gray-200"
              onClick={(e) => {
                e.stopPropagation();
                clearInputAndSelection();
              }}
            >
              <X size={10} />
            </Button>
          )}
        </div>

        <PopoverContent
          className={cn(
            popOverClassName,
            "aero:min-w-80 aero:max-w-96 aero:p-0"
          )}
          align="start"
        >
          <Command className={boxWrapperClassName}>
            <CommandInput
              placeholder={
                inputPlaceholder ? inputPlaceholder : "Search item..."
              }
              className={cn("aero:h-9", boxInputClassName)}
              value={search}
              onValueChange={onHandleChangeSearchInput}
            />
            <CommandList
              className={cn("aero:overflow-y-hidden", boxListClassName)}
            >
              <ScrollArea className="aero:h-72">
                {onLoading ? (
                  <CommandEmpty className="aero:flex aero:flex-col aero:h-1/2 aero:justify-center aero:gap-2 aero:items-center aero:text-muted-foreground">
                    <Loader
                      size={20}
                      className="aero:animate aero:animate-spin"
                    />
                    <span className="aero:text-sm aero:font-semibold">
                      Load data.
                    </span>
                  </CommandEmpty>
                ) : !options.length ? (
                  <CommandEmpty className="aero:flex aero:flex-col aero:items-center aero:justify-center aero:gap-3 aero:py-6 aero:text-center">
                    <span className="aero:text-sm aero:text-neutral-700">
                      No item found.
                    </span>
                    {onCreateOption && (
                      <Button
                        type="button"
                        intent="secondary"
                        className="aero:gap-2"
                        onClick={() => {
                          onCreateOption(search);
                          onSearchChange("");
                          setSearch("");
                          setOpen(false);
                        }}
                      >
                        <CirclePlus className="aero:h-4 aero:w-4" />
                        {createLabel}
                      </Button>
                    )}
                  </CommandEmpty>
                ) : (
                  <CommandGroup className={boxGroupClassName} forceMount={true}>
                    {options.map((option) => (
                      <CommandItem
                        // defaultChecked={defaultChecked === option.value}
                        className={boxItemClassName}
                        value={option.value.toString()}
                        key={option.value}
                        onSelect={() => {
                          if (onChange) {
                            onChange(option);
                          }
                          setOpen(false);
                        }}
                      >
                        {option.label}
                        <Check
                          className={cn(
                            "aero:ml-auto aero:h-4 aero:w-4",
                            option.value === value
                              ? "aero:opacity-100"
                              : "aero:opacity-0"
                          )}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </ScrollArea>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }
);

export { ComboboxAsync };
